import React from 'react';
import { useLocation } from 'react-router-dom';
import { Stack, Button, Theme } from '@mui/material';
import { selectSpecialPage, selectIsAuth } from 'redux/selectors/selectors';
import { useAppSelector, useAppDispatch } from 'redux/hooks/reduxHooks';
import { useCheckPage } from 'shared/hooks/useCheckPage';
import { CustomMuiIcon } from 'assets/images/icons/customMuiIcons';
import { IMenuItem } from 'shared/types/menu.interfaces';
import { MAIN_MENU } from 'shared/constants/menu-constants';
import { urls } from 'shared/constants/routing.constants';
import { useGetAllChallengesQuery } from 'redux/api/challenge.api';
import { useGetAllDebatesQuery } from 'redux/api/debate.api';
import { useGetProfileQuery } from 'redux/api/user.api';
import { Avatar } from 'components/Avatar/Avatar';
import { EAvatarTypes } from 'shared/types/card.interface';
import { openCurrentModal } from 'redux/slices/modals/modals.slice';
import { EModalsNames } from 'shared/types/modal.interface';
import { AUTH_MODAL_DATA } from 'shared/constants/modal-constants';

export const MobileMenu: React.FunctionComponent = () => {
  const location = useLocation();
  const currentPage = useAppSelector(selectSpecialPage);
  const isAuth = useAppSelector(selectIsAuth);
  const dispatch = useAppDispatch();

  const checkPage = useCheckPage(currentPage);
  const { data: profileData = null } = useGetProfileQuery(undefined);

  const { refetch: refetchAllChallenges } = useGetAllChallengesQuery();
  const { refetch: refetchAllDebates } = useGetAllDebatesQuery();

  const mainMenu: IMenuItem[] = [
    {
      name: MAIN_MENU.Debates,
      imageNormal: 'icon-mobile-debates',
      imageSelected: 'icon-mobile-debates-selected',
      onClick: () => checkPage(urls.commonPages.home),
      onUpdate: () => refetchAllDebates(),
      isSelected: location.pathname === urls.commonPages.home,
    },
    {
      name: MAIN_MENU.Challenges,
      imageNormal: 'icon-mobile-challenges',
      imageSelected: 'icon-mobile-challenges-selected',
      onClick: () => checkPage(urls.commonPages.challenges),
      onUpdate: () => refetchAllChallenges(),
      isSelected: location.pathname === urls.commonPages.challenges,
    },
    {
      name: '',
      imageComponent: <CustomMuiIcon type="icon-create-debate" size="44px" />,
      onClick: isAuth
        ? () => dispatch(openCurrentModal({ popupName: EModalsNames.CreateDebateOptions }))
        : () =>
            dispatch(
              openCurrentModal({
                popupName: EModalsNames.AuthModal,
                data: { subtitle: AUTH_MODAL_DATA.subtitleOnChallenge },
              }),
            ),
      isSelected: false,
    },
    {
      name: MAIN_MENU.About,
      imageNormal: 'icon-mobile-about',
      imageSelected: 'icon-mobile-about-selected',
      onClick: () => checkPage(urls.commonPages.about), // вывести уведомление, что страница в разработке
      isSelected: location.pathname === urls.commonPages.about,
    },
    {
      name: MAIN_MENU.Profile,
      imageComponent: isAuth ? (
        <Avatar
          type={EAvatarTypes.MobileMenu}
          firstName={profileData.first_name}
          lastName={profileData.last_name}
        />
      ) : (
        <CustomMuiIcon type="icon-mobile-profile" size="26px" />
      ),
      onClick: isAuth
        ? () => checkPage(urls.myPages.profile)
        : () => dispatch(openCurrentModal({ popupName: EModalsNames.AuthModal })),
      isSelected:
        location.pathname === urls.myPages.profile ||
        location.pathname === urls.myPages.myDebates ||
        location.pathname === urls.myPages.myChallenges,
    },
  ];

  return (
    <Stack
      sx={(theme: Theme) => ({
        flexDirection: 'row',
        width: '100vw',
        padding: 0,
        rowGap: 0,
        boxShadow: `0 2px 24px 0 ${theme.palette.myShadow.menu}`,
        backgroundColor: theme.palette.primary.contrastText,
        display: { md: 'none', sm: 'flex', xs: 'flex' },
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10,
      })}
    >
      {mainMenu.map((item: IMenuItem, index: number) => (
        <Button
          key={index}
          variant="mobileMenu"
          className={`${item.isSelected ? 'selected' : ''}`}
          onClick={() => (item.isSelected ? item.onUpdate?.() : item.onClick())}
          disableRipple
        >
          {item.imageNormal && item.imageSelected ? (
            item.isSelected ? (
              <CustomMuiIcon type={item.imageSelected} size="26px" />
            ) : (
              <CustomMuiIcon type={item.imageNormal} size="26px" />
            )
          ) : (
            item.imageComponent
          )}
          {item.name}
        </Button>
      ))}
    </Stack>
  );
};
